import React, { useEffect, useLayoutEffect, useState, useContext, forwardRef, useImperativeHandle, useRef, useCallback, useMemo } from "react";
import * as THREE from "three";
import {
  CSS2DRenderer,
  CSS2DObject,
} from "three/examples/jsm/renderers/CSS2DRenderer.js";
import CameraControls from 'camera-controls';
import loadCachedImage from "../../imageCache.js";
import { PanoContext } from "../../contexts/PanoContextContainer";

import panoViews from "../constants/panoViews";
import tagPNG from '../../assets/images/tag.svg'
// import navArrowUpYellow from "../../assets/images/nav_arrow_up_yellow.svg";
// import navArrowLeftYellow from "../../assets/images/nav_arrow_left_yellow.svg";
// import navArrowRightYellow from "../../assets/images/nav_arrow_right_yellow.svg";

import LaptopScreenModal from "../components/laptopScreenModal";
import ProductModal from "../productModal";
import Loader from "../components/loader";
import AudioImageModal from "../components/audioImageModal";
// Old museum images - kept for potential future use
import Model3DAudioModal from "../components/model3DAudioModal";
// Note: Old museum audio files removed - not used in new panoramic views
import VideoModal from "../components/videoModal";
import InfoSpotAudioModal from "../components/infoSpotAudioModal";
// import fastingBuddhaModel from '../../assets/Models/Fasting_Buddha.glb'; // Using path string directly if needed, but import is safer for bundlers


import mapIconWhite from '../../assets/svg/map-icon-white.svg'
import mapIconYellow from '../../assets/svg/map-icon-yellow1.svg'
import mapCircle from '../../assets/svg/map-icon-line-white.svg'

import hotspotOuterWhite from '../../assets/svg/hotspot-outer-white.svg'
import hotspotInnerWhite from '../../assets/svg/hotspot-inner-white.svg'
import hotspotInnerYellow from '../../assets/svg/hotspot-inner-yellow.svg'

import "./style.css";

import { TweenMax, Power3 } from 'gsap';
import { AUTO_ROTATE_SPEED, AUTO_ROTATE_ANGLE_RANGES } from '../constants/panoViews';
import { fadeInPlay, fadeOutPause } from '../../utils/audioFade';

/** Base URL for info-spot audio. Place files in public/audio/info-spots/ and use e.g. `${INFO_SPOT_AUDIO_URL}/YourFile.mp3` */
const INFO_SPOT_AUDIO_URL = '/audio/info-spots';

const fortInfoImageContext = require.context('../../assets/images/InforImages', false, /GiriFortInfoImage\.(png|jpe?g|webp|gif|svg)$/i);

const toAssetUrl = (mod) => (typeof mod === 'string' ? mod : mod?.default ?? null);
const normalizeSceneName = (name = '') => name.trim().toLowerCase();

const getFirstAssetFromContext = (ctx) => {
  const firstKey = ctx.keys()[0];
  return firstKey ? toAssetUrl(ctx(firstKey)) : null;
};

THREE.Cache.enabled = true;
const geometry = new THREE.SphereGeometry(500, 60, 40);

let camera, scene, renderer;
let scene2, renderer2, clock, cameraControls;
let moveCamera = "";
let infoPositions = []
let container;
let nre;
let temp;
let blockMultiTouchMoveHandler;

/** Time for pano fade-in (must match TweenMax duration in addPanoViewToScene). Nav hotspots show after this. */
const FADE_DURATION_MS = 1500;
const AUTO_ROTATE_RESUME_DELAY_MS = 2000;
const FORT_INFO_SPOT_NAME = 'giri fort info spot';

/** Set to true to show azimuth/polar angle overlay when moving the 360 pano (for debugging). */
const PANO_DEBUG_ANGLE = false;

const PanoView = forwardRef((props, ref) => {
  const [selectedView, setSelectedView] = useState(undefined);
  const [currentSceneIndex, setCurrentSceneIndex] = useState(-1);
  const [sceneTransitionLoading, setSceneTransitionLoading] = useState(false);
  const [laptopScreenModal, setLaptopScreenModal] = useState({ show: false, title: '', content: '', image: null, images: null, linkButtons: null });
  const [productModal, showProductModal] = useState(undefined);
  const [audioImageModal, setAudioImageModal] = useState({ show: false, audioSrc: null, srtSrc: null, imageSrc: null });
  const [model3DAudioModal, setModel3DAudioModal] = useState({ show: false, modelUrl: null, audioSrc: null, srtSrc: null });
  const [videoModal, setVideoModal] = useState({ show: false, videoSrc: null, srtSrc: null });
  const [infoSpotAudioModal, setInfoSpotAudioModal] = useState({ show: false, title: '', audioSrc: null, srtSrc: null });
  const { setShowLoader, selected, overlayVisible, setHotspotsOutOfView, setTotalHotspotsInScene } = useContext(PanoContext);
  const [bigScreen, setBigScreen] = useState(true)
  const [sceneReady, setSceneReady] = useState(false)
  const initializedRef = useRef(false);
  const loadedCountRef = useRef(0);
  const activeDownloadsRef = useRef(0);
  const hotspotPositionsRef = useRef([]);
  const currentSceneIndexRef = useRef(-1);
  const autoRotatePlayingRef = useRef(false);
  /** Direction of auto-rotate: -1 = forward, +1 = reverse. Flips when boundary is hit. */
  const autoRotateDirRef = useRef(-1);
  /** True while mouse is over an info spot; pauses auto-rotate. */
  const hoveringInfoSpotRef = useRef(false);
  const [debugAngle, setDebugAngle] = useState(null);
  const [introAudioPlaying, setIntroAudioPlaying] = useState(false);
  const [introAudioTime, setIntroAudioTime] = useState(0);
  const [introAudioDuration, setIntroAudioDuration] = useState(0);
  const [introCaptionsEnabled, setIntroCaptionsEnabled] = useState(false);
  const [currentSceneText, setCurrentSceneText] = useState('');
  const [fortInfoModal, setFortInfoModal] = useState({ show: false, zoom: 1 });
  const fortInfoModalRef = useRef(fortInfoModal);
  const fortImageScrollRef = useRef(null);
  const fortImagePinchRef = useRef(null);
  const [fortImageNatural, setFortImageNatural] = useState({ w: 0, h: 0 });
  const [fortImageFitScale, setFortImageFitScale] = useState(1);
  const [audioOverride, setAudioOverride] = useState(null);
  const [isSeekingIntroAudio, setIsSeekingIntroAudio] = useState(false);
  const introAudioRef = useRef(null);
  const introProgressWrapRef = useRef(null);
  const introNeedsUserGestureRef = useRef(false);
  const lastDebugUpdateRef = useRef(0);
  const shouldResumeAutoRotateRef = useRef(false);
  const autoRotateResumeTimeoutRef = useRef(null);
  const pendingAudioRestoreRef = useRef(null);
  const keepSceneAudioPausedAfterRestoreRef = useRef(false);
  const suppressSceneAutoplayRef = useRef(false);
  const fortInfoShouldAutoplayRef = useRef(true);
  const { setCurrent, setAutoRotatePlaying, autoRotatePlaying } = props;
  const sceneAudioMap = useMemo(() => ({}), []);
  const sceneTextMap = useMemo(() => ({}), []);
  const fortInfoImageSrc = useMemo(() => getFirstAssetFromContext(fortInfoImageContext), []);
  const fortInfoAudioSrc = sceneAudioMap[FORT_INFO_SPOT_NAME] || null;
  const fortInfoTextSrc = sceneTextMap[FORT_INFO_SPOT_NAME] || null;
  const currentSceneName = currentSceneIndex >= 0 ? panoViews[currentSceneIndex]?.name : '';
  const currentSceneAudioSrc = sceneAudioMap[normalizeSceneName(currentSceneName)] || null;
  const currentSceneTextSrc = sceneTextMap[normalizeSceneName(currentSceneName)] || null;
  const activeAudioSrc = audioOverride?.audioSrc || currentSceneAudioSrc;
  const activeTextSrc = audioOverride?.textSrc || currentSceneTextSrc;

  useEffect(() => {
    fortInfoModalRef.current = fortInfoModal;
  }, [fortInfoModal]);

  useEffect(() => {
    if (!fortInfoModal.show) {
      setFortImageNatural({ w: 0, h: 0 });
      setFortImageFitScale(1);
    }
  }, [fortInfoModal.show]);

  useLayoutEffect(() => {
    if (!fortInfoModal.show) return;
    const el = fortImageScrollRef.current;
    if (!el || fortImageNatural.w <= 0 || fortImageNatural.h <= 0) return;

    const measure = () => {
      const node = fortImageScrollRef.current;
      if (!node || !fortInfoModalRef.current.show) return;
      const cs = getComputedStyle(node);
      const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
      const padY = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
      const aw = Math.max(1, node.clientWidth - padX);
      const ah = Math.max(1, node.clientHeight - padY);
      const nw = fortImageNatural.w;
      const nh = fortImageNatural.h;
      const s = Math.min(1, aw / nw, ah / nh);
      setFortImageFitScale(s > 0 && Number.isFinite(s) ? s : 1);
    };

    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    return () => ro.disconnect();
  }, [fortInfoModal.show, fortImageNatural.w, fortImageNatural.h]);

  useEffect(() => {
    currentSceneIndexRef.current = currentSceneIndex;
  }, [currentSceneIndex]);

  useEffect(() => {
    autoRotatePlayingRef.current = autoRotatePlaying === true;
  }, [autoRotatePlaying]);

  const clearAutoRotateResumeTimer = useCallback(() => {
    if (autoRotateResumeTimeoutRef.current) {
      clearTimeout(autoRotateResumeTimeoutRef.current);
      autoRotateResumeTimeoutRef.current = null;
    }
  }, []);

  const pauseAutoRotateOnInteraction = useCallback(() => {
    clearAutoRotateResumeTimer();
    if (!setAutoRotatePlaying) return;
    if (autoRotatePlayingRef.current) {
      shouldResumeAutoRotateRef.current = true;
      setAutoRotatePlaying(false);
    } else {
      shouldResumeAutoRotateRef.current = false;
    }
  }, [clearAutoRotateResumeTimer, setAutoRotatePlaying]);

  const scheduleAutoRotateResume = useCallback(() => {
    clearAutoRotateResumeTimer();
    if (!setAutoRotatePlaying || !shouldResumeAutoRotateRef.current) return;
    autoRotateResumeTimeoutRef.current = setTimeout(() => {
      if (shouldResumeAutoRotateRef.current) setAutoRotatePlaying(true);
      autoRotateResumeTimeoutRef.current = null;
    }, AUTO_ROTATE_RESUME_DELAY_MS);
  }, [clearAutoRotateResumeTimer, setAutoRotatePlaying]);

  useEffect(() => {
    let mounted = true;
    if (!activeTextSrc) {
      setCurrentSceneText('');
      return () => {
        mounted = false;
      };
    }
    fetch(activeTextSrc)
      .then((response) => (response.ok ? response.text() : ''))
      .then((text) => {
        if (!mounted) return;
        setCurrentSceneText(text.trim());
      })
      .catch(() => {
        if (mounted) setCurrentSceneText('');
      });
    return () => {
      mounted = false;
    };
  }, [activeTextSrc]);

  const tryPlayIntroAudio = (resetTime = false) => {
    const audio = introAudioRef.current;
    if (!audio) return;
    if (resetTime) audio.currentTime = 0;
    fadeInPlay(audio, 140, 1).then((played) => {
      if (!played) throw new Error('play blocked');
      introNeedsUserGestureRef.current = false;
      setIntroAudioPlaying(true);
    }).catch(() => {
      // Most likely autoplay policy blocked unmuted playback.
      introNeedsUserGestureRef.current = true;
      setIntroAudioPlaying(false);
    });
  };

  useEffect(() => {
    if (activeAudioSrc) return;
    const audio = introAudioRef.current;
    if (!audio) return;
    fadeOutPause(audio, 120, 1).finally(() => setIntroAudioPlaying(false));
  }, [activeAudioSrc]);

  useEffect(() => {
    if (!activeAudioSrc) return;
    if (pendingAudioRestoreRef.current) return;
    if (audioOverride && !fortInfoShouldAutoplayRef.current) return;
    if (suppressSceneAutoplayRef.current && !audioOverride) return;
    tryPlayIntroAudio(true);
  }, [activeAudioSrc, audioOverride]);

  useEffect(() => {
    if (!activeAudioSrc) return;
    if (!introNeedsUserGestureRef.current) return;
    const resumeAfterGesture = () => {
      if (!introNeedsUserGestureRef.current) return;
      tryPlayIntroAudio(false);
    };

    window.addEventListener('pointerdown', resumeAfterGesture, { passive: true });
    window.addEventListener('touchstart', resumeAfterGesture, { passive: true });
    window.addEventListener('keydown', resumeAfterGesture);

    return () => {
      window.removeEventListener('pointerdown', resumeAfterGesture);
      window.removeEventListener('touchstart', resumeAfterGesture);
      window.removeEventListener('keydown', resumeAfterGesture);
    };
  }, [activeAudioSrc, introAudioPlaying]);

  useEffect(() => () => {
    clearAutoRotateResumeTimer();
    const audio = introAudioRef.current;
    if (audio) fadeOutPause(audio, 120, 1);
  }, [clearAutoRotateResumeTimer]);

  const toggleIntroAudioPlayback = () => {
    const audio = introAudioRef.current;
    if (!audio) return;
    if (audio.paused) {
      suppressSceneAutoplayRef.current = false;
      if (audioOverride) fortInfoShouldAutoplayRef.current = true;
      fadeInPlay(audio, 140, 1).then((played) => {
        if (played) setIntroAudioPlaying(true);
      });
    } else {
      if (audioOverride) fortInfoShouldAutoplayRef.current = false;
      fadeOutPause(audio, 120, 1).finally(() => setIntroAudioPlaying(false));
    }
  };

  const openFortInfoExperience = useCallback(() => {
    const audio = introAudioRef.current;
    if (audio) {
      pendingAudioRestoreRef.current = {
        wasPlaying: !audio.paused,
        time: audio.currentTime || 0,
      };
      fadeOutPause(audio, 120, 1).finally(() => setIntroAudioPlaying(false));
    } else {
      pendingAudioRestoreRef.current = { wasPlaying: false, time: 0 };
    }
    setAudioOverride({
      audioSrc: fortInfoAudioSrc,
      textSrc: fortInfoTextSrc,
    });
    setFortInfoModal({ show: true, zoom: 1 });
  }, [fortInfoAudioSrc, fortInfoTextSrc]);

  const closeFortInfoExperience = useCallback(() => {
    const audio = introAudioRef.current;
    fortInfoShouldAutoplayRef.current = !!(audio && !audio.paused);
    setFortInfoModal((prev) => ({ ...prev, show: false, zoom: 1 }));
    keepSceneAudioPausedAfterRestoreRef.current = true;
    suppressSceneAutoplayRef.current = true;
    setAudioOverride(null);
  }, []);

  const changeFortImageZoom = useCallback((nextZoom) => {
    setFortInfoModal((prev) => ({ ...prev, zoom: Math.min(Math.max(nextZoom, 1), 4) }));
  }, []);

  useLayoutEffect(() => {
    if (!fortInfoModal.show) return;
    const n = fortImageScrollRef.current;
    if (!n) return;
    n.scrollLeft = 0;
    n.scrollTop = 0;
  }, [fortInfoModal.show, fortImageNatural.w, fortImageNatural.h, fortImageFitScale]);

  useEffect(() => {
    if (!fortInfoModal.show) return;
    const el = fortImageScrollRef.current;
    if (!el) return;

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        const a = e.touches[0];
        const b = e.touches[1];
        const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
        if (d > 0) {
          fortImagePinchRef.current = { d0: d, zoom0: fortInfoModalRef.current.zoom };
        }
      }
    };

    const onTouchMove = (e) => {
      if (e.touches.length >= 2 && fortImagePinchRef.current) {
        e.preventDefault();
        const a = e.touches[0];
        const b = e.touches[1];
        const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
        if (d <= 0) return;
        const { d0, zoom0 } = fortImagePinchRef.current;
        const next = zoom0 * (d / d0);
        setFortInfoModal((prev) => ({
          ...prev,
          zoom: Math.min(Math.max(next, 1), 4),
        }));
      }
    };

    const endPinch = (e) => {
      if (!e || e.touches.length < 2) fortImagePinchRef.current = null;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', endPinch);
    el.addEventListener('touchcancel', endPinch);

    return () => {
      fortImagePinchRef.current = null;
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', endPinch);
      el.removeEventListener('touchcancel', endPinch);
    };
  }, [fortInfoModal.show]);

  const seekIntroAudioByClientX = useCallback((clientX) => {
    const audio = introAudioRef.current;
    const progressEl = introProgressWrapRef.current;
    if (!audio || !progressEl || !introAudioDuration) return;
    const rect = progressEl.getBoundingClientRect();
    if (rect.width <= 0) return;
    const percent = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
    audio.currentTime = percent * introAudioDuration;
    setIntroAudioTime(audio.currentTime);
  }, [introAudioDuration]);

  const handleIntroAudioSeek = (event) => {
    seekIntroAudioByClientX(event.clientX);
  };

  const handleIntroProgressPointerDown = (event) => {
    seekIntroAudioByClientX(event.clientX);
    setIsSeekingIntroAudio(true);
  };

  useEffect(() => {
    if (!isSeekingIntroAudio) return;
    const onPointerMove = (event) => {
      seekIntroAudioByClientX(event.clientX);
    };
    const stopSeeking = () => {
      setIsSeekingIntroAudio(false);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', stopSeeking);
    window.addEventListener('pointercancel', stopSeeking);

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', stopSeeking);
      window.removeEventListener('pointercancel', stopSeeking);
    };
  }, [isSeekingIntroAudio, seekIntroAudioByClientX]);

  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds)) return '00:00';
    const safeSeconds = Math.max(0, Math.floor(seconds));
    const mins = Math.floor(safeSeconds / 60);
    const secs = safeSeconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const introProgress = introAudioDuration > 0
    ? Math.min((introAudioTime / introAudioDuration) * 100, 100)
    : 0;
  const visibleSceneText = introCaptionsEnabled ? currentSceneText : '';

  useImperativeHandle(
    ref,
    () => ({
      changeScene(i) {
        setSceneTransitionLoading(true);
        setCurrentSceneIndex(i);
        setSelectedView(panoViews[i]);
        setTimeout(() => {
          while (scene.children.length - 1) {
            scene.remove(scene.children[0]);
          }
          cameraControls.reset(false)
          const view = panoViews[i];
          let azimuth = view.initialRotation[0], polar = view.initialRotation[1];
          if (i === 0 && !bigScreen) {
            if (view.initialRotationMobile) {
              azimuth = view.initialRotationMobile[0];
              polar = (view.initialRotationMobile[1] !== undefined) ? view.initialRotationMobile[1] : polar;
            } else {
              azimuth = view.initialRotation[0] + 25;
            }
          }
          cameraControls.rotate(azimuth * THREE.MathUtils.DEG2RAD, polar * THREE.MathUtils.DEG2RAD, false)
        }, 100);
      },

      openInfoSpot(i, j) {
        setTimeout(() => {
          let info = panoViews[j].infoSpots[i];
          setLaptopScreenModal({
            show: true,
            title: info.solutionModelData.infoSpot || info.solutionModelData.title,
            content: info.solutionModelData.primaryContent || '',
            image: info.solutionModelData.primaryImage || null,
            images: info.solutionModelData.carouselImages || null,
            linkButtons: info.solutionModelData.linkButtons || null,
          });
          let overlay = document.querySelector(".welcome-overlay");
          if (overlay) {
            overlay.style.display = "block";
            overlay.classList.add("modal-overlay");
          }
        }, 300);
      },


      openProductModal(i, j) {
        setTimeout(() => {
          let info = panoViews[j].infoSpots[i].solutionModelData.productDetail
          let modal = (
            <ProductModal
              show={true}
              closeCallback={closeProductModal}
              closeCrossCallback={closeProductModal}
              productDetail={info}
            />
          );

          showProductModal(modal);
          let overlay = document.querySelector(".welcome-overlay")
          overlay.style.display = "block"
          overlay.classList.add("modal-overlay")
        }, 300)
      },

      closeModel3DAudioModal() {
        setModel3DAudioModal({ ...model3DAudioModal, show: false });
      },

      zoomIn() {
        if (!camera) return;
        camera.fov = THREE.MathUtils.clamp(camera.fov - 3, 10, 75);
        camera.updateProjectionMatrix();
      },
      zoomOut() {
        if (!camera) return;
        camera.fov = THREE.MathUtils.clamp(camera.fov + 3, 10, 75);
        camera.updateProjectionMatrix();
      },
      resetView() {
        const idx = currentSceneIndexRef.current;
        if (!cameraControls || idx < 0 || !panoViews[idx]) return;
        const view = panoViews[idx];
        cameraControls.reset(false);
        cameraControls.rotate(view.initialRotation[0] * THREE.MathUtils.DEG2RAD, view.initialRotation[1] * THREE.MathUtils.DEG2RAD, false);
      },
      startPanLeft() { moveCamera = 'left'; },
      startPanRight() { moveCamera = 'right'; },
      startPanUp() { moveCamera = 'top'; },
      startPanDown() { moveCamera = 'down'; },
      stopPan() { moveCamera = ''; },
    }),
  )





  useEffect(() => {
    if (window.innerWidth < 768) {
      setBigScreen(false)
    }
    else {
      setBigScreen(true)
    }
    
    if (!initializedRef.current) {
      init();
      animate();
      initializedRef.current = true;
      setSceneReady(true);
    }
    
    // Get the initial selected view index
    const initialIndex = selected !== undefined ? selected : 0;
    const initialView = panoViews[initialIndex];
    
    // Collect all linked image indices from navigation spots
    const linkedIndices = new Set();
    if (initialView.links && initialView.links.length > 0) {
      initialView.links.forEach(link => {
        linkedIndices.add(link.linkTo);
      });
    }
    
    // Create arrays for prioritized loading
    const priorityImages = []; // First image + linked images
    const remainingImages = []; // All other images
    
    panoViews.forEach((view, index) => {
      if (index === initialIndex) {
        // First image - highest priority
        priorityImages.unshift({ view, index });
      } else if (linkedIndices.has(index)) {
        // Linked images - high priority
        priorityImages.push({ view, index });
      } else {
        // Remaining images - lower priority
        remainingImages.push({ view, index });
      }
    });
    
    let firstImageLoaded = false;
    loadedCountRef.current = 0;
    
    // Load first image immediately with highest priority
    if (priorityImages.length > 0) {
      const firstImage = priorityImages[0];
      let firstImageCounted = false;
      
      loadCachedImage(
        firstImage.view.panoImage,
        () => {
          if (!firstImageLoaded) {
            firstImageLoaded = true;
            setCurrentSceneIndex(initialIndex);
            setSelectedView(panoViews[initialIndex]);
            if (cameraControls) {
              const view = panoViews[initialIndex];
              const isMobile = window.innerWidth < 768;
              let azimuth = view.initialRotation[0], polar = view.initialRotation[1] || 0;
              if (initialIndex === 0 && isMobile) {
                if (view.initialRotationMobile) {
                  azimuth = view.initialRotationMobile[0];
                  polar = (view.initialRotationMobile[1] !== undefined) ? view.initialRotationMobile[1] : polar;
                } else {
                  azimuth = view.initialRotation[0] + 25;
                }
              }
              cameraControls.reset(false);
              cameraControls.rotate(azimuth * THREE.MathUtils.DEG2RAD, polar * THREE.MathUtils.DEG2RAD, false);
            }
            // Hide loader immediately when first image loads
            setShowLoader(false);
          }
          if (!firstImageCounted) {
            firstImageCounted = true;
            loadedCountRef.current++;
          }
        },
        (event) => {
          if (event.loaded === event.total && !firstImageCounted) {
            firstImageCounted = true;
            loadedCountRef.current++;
          }
        }
      );
    }
    
    // Preload linked images (priority queue)
    // Use a queue to limit concurrent downloads (browsers typically allow 6 per domain)
    const maxConcurrent = 4; // Leave room for other resources
    activeDownloadsRef.current = 0;
    const downloadQueue = [...priorityImages.slice(1), ...remainingImages];
    const loadingSet = new Set(); // Track which images are currently loading to avoid duplicates
    
    const processQueue = () => {
      while (activeDownloadsRef.current < maxConcurrent && downloadQueue.length > 0) {
        const { view } = downloadQueue.shift();
        const imageUrl = view.panoImage;
        
        // Skip if already loading or cached
        if (loadingSet.has(imageUrl) || THREE.Cache.get(imageUrl) !== undefined) {
          continue;
        }
        
        activeDownloadsRef.current++;
        loadingSet.add(imageUrl);
        let imageLoaded = false;
        
        loadCachedImage(
          imageUrl,
          () => {
            if (!imageLoaded) {
              imageLoaded = true;
              activeDownloadsRef.current--;
              loadingSet.delete(imageUrl);
              loadedCountRef.current++;
              // Process next in queue
              processQueue();
            }
          },
          (event) => {
            if (event.loaded === event.total && !imageLoaded) {
              imageLoaded = true;
              activeDownloadsRef.current--;
              loadingSet.delete(imageUrl);
              loadedCountRef.current++;
              // Process next in queue
              processQueue();
            }
          }
        );
      }
    };
    
    // Start processing queue after a small delay to let first image start loading
    setTimeout(() => {
      processQueue();
    }, 50);
    
    // Fallback: ensure all remaining images load (in case queue processing missed any)
    setTimeout(() => {
      downloadQueue.forEach(({ view }) => {
        const imageUrl = view.panoImage;
        if (!loadingSet.has(imageUrl) && THREE.Cache.get(imageUrl) === undefined) {
          loadCachedImage(
            imageUrl,
            () => {
              loadedCountRef.current++;
            },
            (event) => {
              if (event.loaded === event.total) {
                loadedCountRef.current++;
              }
            }
          );
        }
      });
    }, 2000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, setShowLoader]);

  useEffect(() => {
    if (selectedView === undefined || !sceneReady || scene2 == null) return;

    let hotSpots = [];
    
    const loadScene = async () => {
      if (scene2 == null) return;
      scene2.clear();
      await addPanoViewToScene(selectedView.panoImage);
      // Wait for fade-in to finish so current scene is fully gone and new scene is visible before showing hotspots
      await new Promise((r) => setTimeout(r, FADE_DURATION_MS));
      setSceneTransitionLoading(false);

      if (selectedView.infoSpots) {
        infoPositions = []
        selectedView.infoSpots.forEach((v) => {
          infoPositions.push({
            pos: v.position.clone(),
            name: v.solutionModelData.infoSpot
          })
        })
        selectedView.infoSpots.forEach(addInfoPoint);
      }
      else {
        infoPositions = []
      }

      if (selectedView.links && selectedView.links.length > 0) {
        selectedView.links.forEach((link) => {
          infoPositions.push({
            pos: link.position.clone(),
            name: link.name || 'nav'
          })
          let hotSpot = addLinkPoint(link);
          const handleClick = () => {
            setSceneTransitionLoading(true);
            setCurrent(link.linkTo);
            setCurrentSceneIndex(link.linkTo);
            setSelectedView(panoViews[link.linkTo]);

            setTimeout(() => {
              while (scene.children.length - 1) {
                scene.remove(scene.children[0]);
              }
              cameraControls.reset(false)
              const view = panoViews[link.linkTo];
              let azimuth = view.initialRotation[0], polar = view.initialRotation[1];
              if (link.linkTo === 0 && !bigScreen) {
                if (view.initialRotationMobile) {
                  azimuth = view.initialRotationMobile[0];
                  polar = (view.initialRotationMobile[1] !== undefined) ? view.initialRotationMobile[1] : polar;
                } else {
                  azimuth = view.initialRotation[0] + 25;
                }
              }
              cameraControls.rotate(azimuth * THREE.MathUtils.DEG2RAD, polar * THREE.MathUtils.DEG2RAD, false)
            }, 100);
          };
          
          hotSpot.addEventListener("click", handleClick);
          hotSpots.push({ element: hotSpot, handler: handleClick });
        });
      }

      hotspotPositionsRef.current = infoPositions;
    };

    loadScene();
    
    return () => {
      hotSpots.forEach(({ element, handler }) => {
        element.removeEventListener("click", handler);
      });
      hotspotPositionsRef.current = [];
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedView, bigScreen, setCurrent, sceneReady]);





  const addPanoViewToScene = (url) => {
    return new Promise((resolve, reject) => {
      loadCachedImage(
        url,
        (image) => {
          const texture = new THREE.Texture();
          texture.image = image;
          texture.needsUpdate = true;
          texture.mapping = THREE.EquirectangularReflectionMapping;
          scene.background = texture;
          const material = new THREE.MeshBasicMaterial({ map: texture, transparent: true, opacity: 0 });
          const mesh = new THREE.Mesh(geometry, material);
          scene.add(mesh);
          TweenMax.to(material, 1.5, { opacity: 1 });
          resolve();
        },
        undefined,
        reject
      );
    });
  };

  const closeProductModal = () => {
    showProductModal(undefined);
    let overlay = document.querySelector(".welcome-overlay")
    overlay.style.display = "none"
    overlay.classList.remove("modal-overlay")
  }

  const addInfoPoint = (info) => {
    let infoSpot = document.createElement("div");
    infoSpot.setAttribute('data-hotspot-type', info.solutionModelData.hotspotType);
    infoSpot.setAttribute('data-hotspot-room', info.solutionModelData.hotspotRoom);
    infoSpot.setAttribute('data-hotspot-product', info.solutionModelData.hotspotProduct);
    infoSpot.className = "info-spot-wrap hotspot";

    const iconStack = document.createElement("div");
    iconStack.className = "infospot-icon-stack";

    const iconOuter = document.createElement("img");
    iconOuter.className = "infospot-icon infospot-icon--outer";
    iconOuter.src = hotspotOuterWhite;
    iconOuter.alt = "";

    const iconInnerWhite = document.createElement("img");
    iconInnerWhite.className = "infospot-icon infospot-icon--inner-white";
    iconInnerWhite.src = hotspotInnerWhite;
    iconInnerWhite.alt = "";

    const iconInnerYellow = document.createElement("img");
    iconInnerYellow.className = "infospot-icon infospot-icon--inner-yellow";
    iconInnerYellow.src = hotspotInnerYellow;
    iconInnerYellow.alt = "";

    iconStack.appendChild(iconOuter);
    iconStack.appendChild(iconInnerWhite);
    iconStack.appendChild(iconInnerYellow);
    infoSpot.appendChild(iconStack);

    infoSpot.onmouseenter = () => {
      infoSpot.style.zIndex = "10";
      hoveringInfoSpotRef.current = true;
    };

    infoSpot.onmouseleave = () => {
      infoSpot.style.zIndex = "";
      hoveringInfoSpotRef.current = false;
    };
    infoSpot.addEventListener("click", () => {
      const data = info.solutionModelData;
      if (data.infoSpotType === 'fortImageAudio') {
        openFortInfoExperience();
        return;
      }
      if (data.infoSpotType === 'audioTranscript' && data.audioFolder != null && data.audioFile != null) {
        const folder = encodeURIComponent(data.audioFolder);
        const file = encodeURIComponent(data.audioFile);
        setInfoSpotAudioModal({
          show: true,
          title: data.infoSpot,
          audioSrc: `${INFO_SPOT_AUDIO_URL}/${folder}/${file}.mp3`,
          srtSrc: `${INFO_SPOT_AUDIO_URL}/${folder}/${file}.srt`,
        });
        return;
      }
      if (info.solutionModelData.infoSpot === "Miracle of Śrāvastī Info" || info.solutionModelData.infoSpot === "Miracle of Śrāvastī") {
        // Open video player with SRT subtitles
        setVideoModal({
          show: true,
          videoSrc: '/1. The Miracles at Śrāvastī.mp4',
          srtSrc: '/1. The Miracles at Śrāvastī.srt'
        });
      } else if (info.solutionModelData.infoSpot === "Hariti & Panchika info" || info.solutionModelData.infoSpot === "Hariti & Panchika") {
        // Open audio player with animated image
        // To use new audio: add MP3 to public/audio/info-spots/ and set audioSrc: `${INFO_SPOT_AUDIO_URL}/filename.mp3`
        setAudioImageModal({
          show: true,
          audioSrc: null, // haritiPanchikaAudio - file no longer exists
          srtSrc: '/Hariti & Panchika.srt',
          imageSrc: null
        });
      } else if (info.solutionModelData.infoSpot === "Standing sculptures of Buddha Info" || info.solutionModelData.infoSpot === "Standing sculptures of Buddha") {
        // Open audio player with animated image for Standing sculptures of Buddha
        // Note: Old museum content - audio file removed
        setAudioImageModal({
          show: true,
          audioSrc: null, // standingBuddhaAudio - file no longer exists
          srtSrc: '/Images of the Buddha in Gandhara Art.srt',
          imageSrc: null
        });
      } else if (info.solutionModelData.infoSpot === "Fasting Siddhartha") {
        // Open 3D model with audio
        // Note: Old museum content - audio file removed
        setModel3DAudioModal({
          show: true,
          modelUrl: '/Fasting_Buddha.glb', // Assuming it's in public folder or handled by loader
          audioSrc: null, // fastingBuddhaAudio - file no longer exists
          srtSrc: '/3. The Fasting Siddhartha.srt' // Assuming it's in public folder
        });
      } else {
        // Open laptop screen modal (info hotspot content on laptop graphic)
        setLaptopScreenModal({
          show: true,
          title: info.solutionModelData.infoSpot || info.solutionModelData.title,
          content: info.solutionModelData.primaryContent || '',
          image: info.solutionModelData.primaryImage || null,
          images: info.solutionModelData.carouselImages || null,
          linkButtons: info.solutionModelData.linkButtons || null,
        });
      }
      // let overlay = document.querySelector(".welcome-overlay")
      // overlay.style.display="block"
      // overlay.classList.add("modal-overlay")
    });
    let object = new CSS2DObject(infoSpot);
    object.position.copy(info.position);

    if (info.scale) object.scale.copy(info.scale);

    if (info.rotation) {
      object.rotation.x = info.rotation.x;
      object.rotation.y = info.rotation.y;
      object.rotation.z = info.rotation.z;
    }

    scene2.add(object);
    scene2.add(nre)



    return infoSpot;
  };

  const addLinkPoint = (link) => {
    let hotSpot = document.createElement("div");
    hotSpot.className = "hotspot";
    hotSpot.setAttribute('data-hotspot-type', 'location');
    hotSpot.setAttribute('data-hotspot-room', link.hotspotRoom);
    if (link.invisible) hotSpot.classList.add("hotspot-invisible");

    if (link.backgroundImage && link.image) {
      const panelWrap = document.createElement("div");
      panelWrap.className = "link-panel-wrap";
      panelWrap.style.backgroundImage = `url(${link.backgroundImage})`;
      const panelIcon = document.createElement("img");
      panelIcon.className = "link-panel-icon";
      panelIcon.src = link.image;
      panelIcon.alt = link.name;
      panelWrap.appendChild(panelIcon);
      const namePlate = document.createElement("div");
      namePlate.className = "name-plate";
      const namePlateTag = document.createElement("img");
      namePlateTag.src = tagPNG;
      namePlateTag.className = "name-plate-tag";
      const namePlateTag1 = document.createElement("div");
      namePlateTag1.className = "name-plate-tag1";
      const text = document.createElement("p");
      text.style.paddingTop = "0px";
      text.style.paddingBottom = "0px";
      text.innerHTML = link.name;
      namePlateTag1.appendChild(text);
      namePlate.appendChild(namePlateTag1);
      namePlate.appendChild(namePlateTag);
      hotSpot.appendChild(panelWrap);
      hotSpot.appendChild(namePlate);
      if (!bigScreen) {
        namePlate.style.display = "block";
        namePlate.style.opacity = "1";
      } else {
        namePlate.style.display = "none";
        namePlate.style.opacity = "0";
      }
      hotSpot.onmouseenter = () => {
        panelIcon.classList.add("link-panel-icon-hover");
        if (bigScreen) {
          namePlate.style.display = "block";
          TweenMax.to(namePlate, 0.4, { opacity: 1, x: 10, ease: Power3.easeIn() });
        }
      };
      hotSpot.onmouseleave = () => {
        panelIcon.classList.remove("link-panel-icon-hover");
        if (bigScreen) {
          TweenMax.to(namePlate, 0.4, { opacity: 0, x: -10, ease: Power3.easeOut(), onComplete: () => { namePlate.style.display = "none"; } });
        }
      };
      const object = new CSS2DObject(hotSpot);
      object.position.copy(link.position);
      if (link.scale) object.scale.copy(link.scale);
      if (link.rotation) {
        object.rotation.x = link.rotation.x;
        object.rotation.y = link.rotation.y;
        object.rotation.z = link.rotation.z;
      }
      scene2.add(object);
      return hotSpot;
    }

    const showTitle = link.showTitle !== false;
    let namePlate = null;
    if (showTitle) {
      namePlate = document.createElement("div");
      let namePlateTag = document.createElement("img");
      namePlateTag.src = tagPNG;
      namePlateTag.className = "name-plate-tag";
      namePlate.className = "name-plate";
      let namePlateTag1 = document.createElement("div");
      namePlateTag1.className = "name-plate-tag1";
      let text = document.createElement("p");
      text.style.paddingTop = "0px";
      text.style.paddingBottom = "0px";
      text.innerHTML = link.name;
      namePlateTag1.appendChild(text);
      namePlate.appendChild(namePlateTag1);
      namePlate.appendChild(namePlateTag);
      hotSpot.appendChild(namePlate);
    }
    const linkImage = link.image || mapIconWhite;
    const linkImageHover = link.image || mapIconYellow;
    var anim = document.createElement("img");
    anim.className = link.image ? "vert-move-white link-custom-image" + (link.imageScale === 2 ? " link-custom-image-2x" : "") : "vert-move-white";
    anim.src = linkImage;
    hotSpot.appendChild(anim);

    var animHover = document.createElement("img");
    animHover.className = link.image ? "vert-move-yellow hotspot link-custom-image" + (link.imageScale === 2 ? " link-custom-image-2x" : "") : "vert-move-yellow hotspot";
    animHover.setAttribute('data-hotspot-type', 'location');
    animHover.setAttribute('data-hotspot-room', link.hotspotRoom);
    animHover.classList.add("hide");
    animHover.src = linkImageHover;
    hotSpot.appendChild(animHover);

    var animHoverCircle = document.createElement("img");
    animHoverCircle.className = "map-circle";
    animHoverCircle.classList.add("hide");
    if (link.image) animHoverCircle.style.display = "none";
    animHoverCircle.src = mapCircle;
    hotSpot.appendChild(animHoverCircle);

    if (showTitle) {
      if (!bigScreen) {
        anim.style.display = "none";
        animHover.style.display = "block";
        animHoverCircle.style.display = "block";
        TweenMax.to(namePlate, 0.4, { opacity: 1, x: 10, display: "block", ease: Power3.easeIn() });
      } else {
        anim.style.display = "block";
        animHover.style.display = "none";
        animHoverCircle.style.display = "none";
        TweenMax.to(namePlate, 0.4, { opacity: 0, x: -10, display: "none", ease: Power3.easeOut() });
      }
    }

    hotSpot.onmouseenter = async () => {
      anim.style.display = "none";
      animHover.style.display = "block";
      animHoverCircle.style.display = "block";
      if (showTitle && namePlate) {
        TweenMax.to(namePlate, 0.4, { opacity: 1, x: 10, display: "block", ease: Power3.easeIn() });
      }
    };
    hotSpot.onmouseleave = async () => {
      anim.style.display = "block";
      animHover.style.display = "none";
      animHoverCircle.style.display = "none";
      if (showTitle && namePlate) {
        TweenMax.to(namePlate, 0.4, { opacity: 0, x: -10, display: "none", ease: Power3.easeOut() });
      }
    };

    let object = new CSS2DObject(hotSpot);
    object.position.copy(link.position);

    if (link.scale) object.scale.copy(link.scale);

    if (link.rotation) {
      object.rotation.x = link.rotation.x;
      object.rotation.y = link.rotation.y;
      object.rotation.z = link.rotation.z;
    }

    scene2.add(object);
    return hotSpot;
  };

  function init() {
    container = document.getElementById("infoContainer");
    camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      1,
      10000
    );
    camera.position.z = 0.01;
    camera.fov = THREE.MathUtils.clamp(100, 10, 75)
    camera.updateProjectionMatrix();

    scene = new THREE.Scene();
    scene2 = new THREE.Scene();

    geometry.scale(-1, 1, 1);

    renderer = new THREE.WebGLRenderer();
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.domElement.style.zIndex = 2;
    renderer.domElement.style.flex = "1 1 auto";
    renderer.domElement.style.overflow = "hidden";

    container.appendChild(renderer.domElement);
    container.style.touchAction = "none";
    container.addEventListener("pointerup", onPointerUp, false);
    container.addEventListener("pointerup", onPointerDown, false);

    renderer2 = new CSS2DRenderer();
    renderer2.setSize(window.innerWidth, window.innerHeight);
    renderer2.domElement.style.position = "absolute";
    renderer2.domElement.style.top = 0;
    renderer2.domElement.style.zIndex = 4;
    container.appendChild(renderer2.domElement);
    var test = document.createElement("div")
    test.className = "test-div"
    test.innerHTML = `Bilal`
    nre = new CSS2DObject(test);
    clock = new THREE.Clock();
    const EPS = 1e-5;
    CameraControls.install({ THREE: THREE });
    cameraControls = new CameraControls(camera, renderer2.domElement);
    cameraControls.azimuthRotateSpeed = - 0.2; // negative value to invert rotation direction
    cameraControls.polarRotateSpeed = - 0.2; // negative value to invert rotation direction
    cameraControls.truckSpeed = 1 / EPS * 3;
    cameraControls.mouseButtons.right = CameraControls.ACTION.NONE
    cameraControls.mouseButtons.wheel = CameraControls.ACTION.NONE
    // Mobile UX: disable pinch/two-finger gestures to avoid hotspot/nav displacement.
    cameraControls.touches.two = CameraControls.ACTION.NONE;
    cameraControls.touches.three = CameraControls.ACTION.NONE;
    blockMultiTouchMoveHandler = (event) => {
      if (event.touches && event.touches.length > 1) {
        event.preventDefault();
      }
    };
    container.addEventListener("touchmove", blockMultiTouchMoveHandler, { passive: false });
    renderer2.domElement.addEventListener("touchmove", blockMultiTouchMoveHandler, { passive: false });
    renderer2.domElement.addEventListener("pointerdown", pauseAutoRotateOnInteraction, { passive: true });
    renderer2.domElement.addEventListener("pointerup", scheduleAutoRotateResume, { passive: true });
    renderer2.domElement.addEventListener("pointercancel", scheduleAutoRotateResume, { passive: true });
    renderer2.domElement.addEventListener("wheel", () => {
      pauseAutoRotateOnInteraction();
      scheduleAutoRotateResume();
    }, { passive: true });
    document.addEventListener("wheel", onDocumentMouseWheel, false);
    window.addEventListener("resize", onWindowResize, false);
  }

  function onWindowResize() {
    // camera.updateProjectionMatrix();
    // renderer.setSize(window.innerWidth, window.innerHeight);
    // renderer2.setSize(window.innerWidth, window.innerHeight);
    if (window.innerWidth < 768) {
      setBigScreen(false)
    }
    else {
      setBigScreen(true)
    }
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer2.setSize(window.innerWidth, window.innerHeight);
    renderer.setSize(window.innerWidth, window.innerHeight);

  }

  function onPointerDown(event, allPositions) {
    if (event.isPrimary === false)
      return;
    document.addEventListener("pointerup", (e) => onPointerUp(e, allPositions), false);
    // updateNavigation(allPositions)
  }

  function onPointerUp(event, allPositions) {
    if (event.isPrimary === false) return;
    document.removeEventListener("pointerup", onPointerUp);
    moveCamera = ""
    // updateNavigation(allPositions)
  }

  function onDocumentMouseWheel(event) {
    // Wheel handler - functionality commented out
    // if (!!!modal && !!!attachmentModal && !!!solutionModal && mapModal.classList[1]=="fadeOut" && overlay.style.display=='none' && !sidebar.classList.contains("is-open") && filterResults==null && getInTouch.style.display=="none" && filter==null) {
    //   const fov = camera.fov + event.deltaY * 0.05;
    //   if (fov < 50) return;
    //   camera.fov = THREE.MathUtils.clamp(fov, 10, 75);
    //   camera.updateProjectionMatrix();
    // }
  }

  const updateCameraLookAt = (delta) => {
    if (moveCamera === "left") {
      cameraControls.rotate(1 * THREE.MathUtils.DEG2RAD, 0, true);
    } else if (moveCamera === "right") {
      cameraControls.rotate(-1 * THREE.MathUtils.DEG2RAD, 0, true);
    } else if (moveCamera === "top") {
      cameraControls.rotate(0, 1 * THREE.MathUtils.DEG2RAD, true);
    } else if (moveCamera === "down") {
      cameraControls.rotate(0, -1 * THREE.MathUtils.DEG2RAD, true);
    } else if (autoRotatePlayingRef.current && !hoveringInfoSpotRef.current) {
      const sceneIndex = currentSceneIndexRef.current;
      const range = AUTO_ROTATE_ANGLE_RANGES[sceneIndex];
      if (range) {
        const minRad = range.minDeg * THREE.MathUtils.DEG2RAD;
        const maxRad = range.maxDeg * THREE.MathUtils.DEG2RAD;
        const currentAzimuth = cameraControls.azimuthAngle;
        const deltaRad = autoRotateDirRef.current * AUTO_ROTATE_SPEED * delta * THREE.MathUtils.DEG2RAD;
        const nextAzimuth = currentAzimuth + deltaRad;
        if (nextAzimuth <= minRad) {
          // Hit the min boundary — clamp and reverse to positive direction
          cameraControls.rotate(minRad - currentAzimuth, 0, true);
          autoRotateDirRef.current = 1;
        } else if (nextAzimuth >= maxRad) {
          // Hit the max boundary — clamp and reverse to negative direction
          cameraControls.rotate(maxRad - currentAzimuth, 0, true);
          autoRotateDirRef.current = -1;
        } else {
          cameraControls.rotate(deltaRad, 0, true);
        }
      } else {
        cameraControls.rotate(-AUTO_ROTATE_SPEED * delta * THREE.MathUtils.DEG2RAD, 0, true);
      }
    }
  };

  function animate() {
    requestAnimationFrame(animate);
    const delta = clock.getDelta();
    updateCameraLookAt(delta);
    updateNavigation(hotspotPositionsRef.current);
    cameraControls.update(delta);
    if (PANO_DEBUG_ANGLE && cameraControls && Date.now() - lastDebugUpdateRef.current > 100) {
      lastDebugUpdateRef.current = Date.now();
      setDebugAngle({
        azimuth: THREE.MathUtils.radToDeg(cameraControls.azimuthAngle),
        polar: THREE.MathUtils.radToDeg(cameraControls.polarAngle),
      });
    }
    camera.updateMatrix();
    camera.updateMatrixWorld();
    // object.position.set(new THREE.Vector3(-400, -100, 750))
    renderer.render(scene, camera);
    renderer2.render(scene2, camera);
  }

  const updateNavigation = (allPositions) => {
    var left = 0;
    var right = 0;
    if (!allPositions || allPositions.length === 0) {
      setHotspotsOutOfView(0);
      setTotalHotspotsInScene(0);
      return;
    }
    setTotalHotspotsInScene(allPositions.length);
    camera.updateMatrix();
    camera.updateMatrixWorld();
    var frustum = new THREE.Frustum();
    frustum.setFromProjectionMatrix(new THREE.Matrix4().multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse));
    allPositions.forEach(v => {
      if (!frustum.containsPoint(v.pos)) {
        temp = v.pos.clone();
        camera.worldToLocal(temp);
        if (temp.x < 0) {
          left = left + 1;
        } else {
          right = right + 1;
        }
      }
    });
    setHotspotsOutOfView(left + right);
  }


  return (
    <div id="infoContainer">
      {PANO_DEBUG_ANGLE && debugAngle != null && (
        <div className="pano-debug-angle" aria-hidden="true">
          Azimuth: {debugAngle.azimuth.toFixed(1)}° · Polar: {debugAngle.polar.toFixed(1)}°
        </div>
      )}
      {sceneTransitionLoading && (
        <div className="scene-transition-loader" aria-hidden="true">
          <Loader />
        </div>
      )}
      {activeAudioSrc && (
        <div className="pano-intro-audio-wrap" aria-label="First scene audio player">
          <audio
            ref={introAudioRef}
            src={activeAudioSrc}
            preload="metadata"
            onTimeUpdate={(e) => setIntroAudioTime(e.currentTarget.currentTime || 0)}
            onLoadedMetadata={(e) => {
              const audio = e.currentTarget;
              setIntroAudioDuration(audio.duration || 0);
              if (pendingAudioRestoreRef.current && !audioOverride) {
                const restore = pendingAudioRestoreRef.current;
                pendingAudioRestoreRef.current = null;
                audio.currentTime = Math.min(restore.time || 0, audio.duration || 0);
                setIntroAudioTime(audio.currentTime);
                    fadeOutPause(audio, 100, 1).finally(() => setIntroAudioPlaying(false));
              }
            }}
            onCanPlay={() => {
              if (audioOverride && !fortInfoShouldAutoplayRef.current) {
                return;
              }
              if (suppressSceneAutoplayRef.current && !audioOverride) {
                return;
              }
              if (keepSceneAudioPausedAfterRestoreRef.current && !audioOverride) {
                keepSceneAudioPausedAfterRestoreRef.current = false;
                return;
              }
              if (activeAudioSrc && !introAudioPlaying && !(pendingAudioRestoreRef.current && !audioOverride)) {
                tryPlayIntroAudio(false);
              }
            }}
            onPlay={() => setIntroAudioPlaying(true)}
            onPause={() => setIntroAudioPlaying(false)}
            onEnded={() => setIntroAudioPlaying(false)}
          />
          <div className="pano-intro-audio-card">
            {visibleSceneText && (
              <div className="pano-intro-audio-caption" aria-live="polite">
                {visibleSceneText}
              </div>
            )}
            <div className="pano-welcome-audio-bar">
              <button
                type="button"
                className="pano-welcome-audio-bar__play"
                onClick={toggleIntroAudioPlayback}
                aria-label={introAudioPlaying ? 'Pause audio' : 'Play audio'}
                title={introAudioPlaying ? 'Pause' : 'Play'}
              >
                {introAudioPlaying ? '❚❚' : '▶'}
              </button>
              <div
                ref={introProgressWrapRef}
                className="pano-welcome-audio-bar__progress-wrap"
                onClick={handleIntroAudioSeek}
                onPointerDown={handleIntroProgressPointerDown}
                role="slider"
                aria-label="Audio progress"
                aria-valuemin={0}
                aria-valuemax={Math.round(introAudioDuration || 0)}
                aria-valuenow={Math.round(introAudioTime || 0)}
              >
                <div className="pano-welcome-audio-bar__progress-fill" style={{ width: `${introProgress}%` }} />
              </div>
              <span className="pano-welcome-audio-bar__time">
                {formatTime(introAudioTime)} / {formatTime(introAudioDuration)}
              </span>
              <button
                type="button"
                className={`pano-welcome-audio-bar__cc ${introCaptionsEnabled ? 'pano-welcome-audio-bar__cc--active' : ''}`}
                onClick={() => setIntroCaptionsEnabled((prev) => !prev)}
                aria-label={introCaptionsEnabled ? 'Hide scene text' : 'Show scene text'}
                title={introCaptionsEnabled ? 'CC Off' : 'CC On'}
              >
                {introCaptionsEnabled ? 'CC On' : 'CC Off'}
              </button>
            </div>
          </div>
        </div>
      )}
      {fortInfoModal.show && (
        <div className="fort-info-modal" role="dialog" aria-modal="true" aria-label="Giri Fort info image">
          <button type="button" className="fort-info-modal__backdrop" onClick={closeFortInfoExperience} aria-label="Close image" />
          <div className="fort-info-modal__content">
            <button type="button" className="fort-info-modal__close" onClick={closeFortInfoExperience} aria-label="Close image">
              ×
            </button>
            <div className="fort-info-modal__zoom-controls">
              <button type="button" onClick={() => changeFortImageZoom(fortInfoModal.zoom - 0.25)} aria-label="Zoom out">-</button>
              <span>{Math.round(fortInfoModal.zoom * 100)}%</span>
              <button type="button" onClick={() => changeFortImageZoom(fortInfoModal.zoom + 0.25)} aria-label="Zoom in">+</button>
              <button
                type="button"
                className={`fort-info-modal__audio-toggle ${introAudioPlaying ? 'fort-info-modal__audio-toggle--active' : ''}`}
                onClick={toggleIntroAudioPlayback}
                aria-label={introAudioPlaying ? 'Audio off' : 'Audio on'}
                title={introAudioPlaying ? 'Audio Off' : 'Audio On'}
                disabled={!activeAudioSrc}
              >
                {introAudioPlaying ? 'Audio On' : 'Audio Off'}
              </button>
            </div>
            <div className="fort-info-modal__image-wrap" ref={fortImageScrollRef}>
              {fortInfoImageSrc ? (
                <div className="fort-info-modal__image-inner">
                  <img
                    key={fortInfoImageSrc}
                    src={fortInfoImageSrc}
                    alt="Giri Fort Info"
                    className="fort-info-modal__image"
                    onLoad={(e) => {
                      const img = e.currentTarget;
                      setFortImageNatural({ w: img.naturalWidth, h: img.naturalHeight });
                    }}
                    style={
                      fortImageNatural.w > 0 && fortImageNatural.h > 0
                        ? {
                            width: fortImageNatural.w * fortImageFitScale * fortInfoModal.zoom,
                            height: 'auto',
                            maxWidth: 'none',
                            maxHeight: 'none',
                          }
                        : undefined
                    }
                  />
                </div>
              ) : (
                <p className="fort-info-modal__missing">GiriFortInfoImage not found in InforImages folder.</p>
              )}
            </div>
          </div>
        </div>
      )}
      <div id="navArrows" style={{ display: overlayVisible ? 'none' : 'block' }} />
      {/* {cookiePopup && 
    <div className="cookie-overlay">
    <div className="accept-cookie custom-container">
        <p className="cookie-desc">
        Important information regarding the Hajj Application website: this site uses cookies to improve user experience and stores information on your computer. By continuing to use our site, you consent to our Cookie Policy. If you do not want to enable cookies, review our policy and learn how they can be disabled. Note that disabling cookies will disable some features of the site.
        </p>
        <button className="accept-button" onClick={acceptCookies}>ACCEPT</button>
        </div>
        </div>
        } */}
      {productModal}
      <AudioImageModal
        show={audioImageModal.show}
        onClose={() => setAudioImageModal({ ...audioImageModal, show: false })}
        audioSrc={audioImageModal.audioSrc}
        srtSrc={audioImageModal.srtSrc}
        imageSrc={audioImageModal.imageSrc}
      />

      <Model3DAudioModal
        show={model3DAudioModal.show}
        onClose={() => setModel3DAudioModal({ ...model3DAudioModal, show: false })}
        modelUrl={model3DAudioModal.modelUrl}
        audioSrc={model3DAudioModal.audioSrc}
        srtSrc={model3DAudioModal.srtSrc}
      />

      <VideoModal
        show={videoModal.show}
        onClose={() => setVideoModal({ ...videoModal, show: false })}
        videoSrc={videoModal.videoSrc}
        srtSrc={videoModal.srtSrc}
      />

      <InfoSpotAudioModal
        show={infoSpotAudioModal.show}
        onClose={() => setInfoSpotAudioModal({ ...infoSpotAudioModal, show: false })}
        title={infoSpotAudioModal.title}
        audioSrc={infoSpotAudioModal.audioSrc}
        srtSrc={infoSpotAudioModal.srtSrc}
      />
      <LaptopScreenModal
        show={laptopScreenModal.show}
        onClose={() => setLaptopScreenModal({ ...laptopScreenModal, show: false })}
        title={laptopScreenModal.title}
        content={laptopScreenModal.content}
        image={laptopScreenModal.image}
        images={laptopScreenModal.images}
        linkButtons={laptopScreenModal.linkButtons}
      />
    </div>
  );
})

export default PanoView;
